import React from "react";
import { Form } from "antd";
import { useTranslate } from "@refinedev/core";
import { Input, TextArea } from "@libertify/ui";
import { EditableItemCard } from "../EditableItemCard";

const KEY = "experienceEdit.quickRecallCard";

export interface QuickRecallCardItemProps {
  /** Index of this card within the `cards` Form.List. */
  name: number;
  /** 1-based position for the "Card n" label — follows the list, so the
   *  cards renumber after a removal. */
  position: number;
  onRemove: () => void;
}

/**
 * One card of the Quick recall edit card: its number, a Remove action, and
 * the editable term and definition. The fields are the API's own `front` /
 * `back`, so the form value is the wire shape. Must be rendered inside the
 * card's Form.
 *
 * Removal persists with the next autosave, so a card that carries text asks
 * for confirmation first (same rule as the header links and documents on the
 * edit page); a blank, just-added row goes without asking.
 */
export const QuickRecallCardItem: React.FC<QuickRecallCardItemProps> = ({
  name,
  position,
  onRemove,
}) => {
  const t = useTranslate();
  const front: string = Form.useWatch(["cards", name, "front"]) ?? "";
  const back: string = Form.useWatch(["cards", name, "back"]) ?? "";

  return (
    <EditableItemCard
      label={t(`${KEY}.cardLabel`, { number: position })}
      removeLabel={t(`${KEY}.remove`)}
      removeConfirmLabel={t(`${KEY}.removeConfirm`)}
      confirmRemove={Boolean(front.trim() || back.trim())}
      onRemove={onRemove}
    >
      {/* Registers the row's session id so it rides along in the form
          values — antd only reports registered paths (see
          `ExperienceLinkItem`, which does the same for its link id). */}
      <Form.Item name={[name, "id"]} noStyle>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name={[name, "front"]} noStyle>
        <Input placeholder={t(`${KEY}.term`)} aria-label={t(`${KEY}.term`)} />
      </Form.Item>
      <Form.Item name={[name, "back"]} noStyle>
        <TextArea
          placeholder={t(`${KEY}.definition`)}
          aria-label={t(`${KEY}.definition`)}
          autoSize={{ minRows: 3 }}
        />
      </Form.Item>
    </EditableItemCard>
  );
};

export default QuickRecallCardItem;
